import {
  Activity,
  Archive,
  Building2,
  FileText,
  CalendarDays,
  ChartColumn,
  Flag,
  KeyRound,
  LayoutDashboard,
  LifeBuoy,
  Mail,
  Megaphone,
  MonitorSmartphone,
  Network,
  Package,
  Receipt,
  Scale,
  ScrollText,
  UsersRound,
  type LucideIcon,
} from 'lucide-react';
import type { ComponentType } from 'react';
import type { CopyKey } from '@ticarishift/i18n';
import { PlatformPermissions, type PlatformPermission } from '../shared/auth/permissions.ts';

/**
 * Backoffice rotaları (08 §11.3 deseni; ADR-002, S16): kenar çubuğu ve sayfa başlığı buradan üretilir. Her rota tek bir
 * platform yetkisi ister; menüde yalnız oturumun yetkisi olan sayfalar görünür (yetkiler sunucudan,
 * `GET /api/platform/me`). Sayfalar tembel yüklenir: giriş paketi yalnız kabuğu ve oturum açmayı taşır.
 */
export type RouteDef = {
  readonly id: string;
  readonly path: string;
  readonly title: CopyKey;
  readonly permission: PlatformPermission;
  /** Kenar çubuğunda görünür (sırayla). */
  readonly nav?: { readonly icon: LucideIcon };
  /** Breadcrumb'da üst sayfa. */
  readonly parent?: string;
  readonly lazy: () => Promise<{ Component: ComponentType }>;
};

export const routes: readonly RouteDef[] = [
  {
    id: 'overview',
    path: '/',
    title: 'backoffice.nav.overview',
    permission: PlatformPermissions.SystemRead,
    nav: { icon: LayoutDashboard },
    lazy: async () => ({ Component: (await import('../features/overview/OverviewPage.tsx')).OverviewPage }),
  },
  {
    id: 'tenants',
    path: '/tenants',
    title: 'backoffice.nav.tenants',
    permission: PlatformPermissions.TenantsRead,
    nav: { icon: Building2 },
    lazy: async () => ({ Component: (await import('../features/tenants/TenantsPage.tsx')).TenantsPage }),
  },
  {
    id: 'tenantDetail',
    path: '/tenants/:id',
    title: 'backoffice.nav.tenantDetail',
    permission: PlatformPermissions.TenantsRead,
    parent: 'tenants',
    lazy: async () => ({ Component: (await import('../features/tenants/TenantDetailPage.tsx')).TenantDetailPage }),
  },
  // Kapanan işletmelerin silme kuyruğu (S44, YH-S61): pencere, iade paketi, silme adımları ve imha tutanağı (izleme; eylem yok). Görmek
  // `platform.tenants.read`: kuyruk işletmenin yaşam döngüsünün parçasıdır ve yalnız durum ve sayım taşır.
  {
    id: 'tenantExits',
    path: '/tenant-exits',
    title: 'backoffice.nav.tenantExits',
    permission: PlatformPermissions.TenantsRead,
    nav: { icon: Archive },
    lazy: async () => ({ Component: (await import('../features/tenant-exits/TenantExitsPage.tsx')).TenantExitsPage }),
  },
  {
    id: 'tenantExitDetail',
    path: '/tenant-exits/:tenantId',
    title: 'backoffice.nav.tenantExitDetail',
    permission: PlatformPermissions.TenantsRead,
    parent: 'tenantExits',
    lazy: async () => ({ Component: (await import('../features/tenant-exits/TenantExitDetailPage.tsx')).TenantExitDetailPage }),
  },
  // Plan kataloğu (S41, YH-S57): planlar ve sürümleri, ek paketler, paket × özellik matrisi. Görmek `platform.tenants.read`, yeni sürüm
  // ve plan ataması ekranda `platform.plans.manage`.
  {
    id: 'plans',
    path: '/plans',
    title: 'backoffice.nav.plans',
    permission: PlatformPermissions.TenantsRead,
    nav: { icon: Package },
    lazy: async () => ({ Component: (await import('../features/plans/plans.ts')).PlansPage }),
  },
  // Faturalama izleme ve gelir göstergeleri (S43 bölüm 6, ADR-002 §2.2): abonelikler, faturalar, başarısız ödemeler, uyarılar, müşavir
  // pay raporu. Görmek `platform.billing.read`; havale, iade, yeniden tahsil ve uyarı kapatma ekranda `platform.billing.manage`.
  {
    id: 'billing',
    path: '/billing',
    title: 'backoffice.nav.billing',
    permission: PlatformPermissions.BillingRead,
    nav: { icon: Receipt },
    lazy: async () => ({ Component: (await import('../features/billing/billing.ts')).BillingPage }),
  },
  // QR Ekranı sağlığı (S28; ADR-002 §2.5): işletme başına cihaz sayıları; kişi verisi ve lokasyon adı yok.
  {
    id: 'screenHealth',
    path: '/screen-health',
    title: 'backoffice.nav.screenHealth',
    permission: PlatformPermissions.SystemRead,
    nav: { icon: MonitorSmartphone },
    lazy: async () => ({ Component: (await import('../features/screens/screens.ts')).ScreenHealthPage }),
  },
  // S40 (SK-04, PY-57): kullanım panosu ve sistem sağlığı (yalnız sayım); duyurular ve bakım bildirimi (yazmak
  // `platform.announcements.manage`, görmek `platform.system.read`).
  {
    id: 'usage',
    path: '/usage',
    title: 'backoffice.nav.usage',
    permission: PlatformPermissions.SystemRead,
    nav: { icon: ChartColumn },
    lazy: async () => ({ Component: (await import('../features/usage/usage.ts')).UsagePage }),
  },
  {
    id: 'health',
    path: '/health',
    title: 'backoffice.nav.health',
    permission: PlatformPermissions.SystemRead,
    nav: { icon: Activity },
    lazy: async () => ({ Component: (await import('../features/health/HealthPage.tsx')).HealthPage }),
  },
  {
    id: 'announcements',
    path: '/announcements',
    title: 'backoffice.nav.announcements',
    permission: PlatformPermissions.SystemRead,
    nav: { icon: Megaphone },
    lazy: async () => ({ Component: (await import('../features/announcements/announcements.ts')).AnnouncementsPage }),
  },
  {
    id: 'announcement',
    path: '/announcements/:id',
    title: 'backoffice.nav.announcement',
    permission: PlatformPermissions.SystemRead,
    parent: 'announcements',
    lazy: async () => ({ Component: (await import('../features/announcements/announcements.ts')).AnnouncementPage }),
  },
  // Sahip hesap kurtarma onay kuyruğu (S31, YH-S52): talep açma ve görme yetkisiyle; onay ve ret `platform.owner_recovery.decide`.
  {
    id: 'ownerRecovery',
    path: '/owner-recovery',
    title: 'backoffice.nav.ownerRecovery',
    permission: PlatformPermissions.OwnerRecoveryRequest,
    nav: { icon: KeyRound },
    lazy: async () => ({ Component: (await import('../features/recovery/recovery.ts')).OwnerRecoveryPage }),
  },
  // Destek erişimi (S44, ADR-002 §2.4, SK-04): işletmenin izniyle salt okuma destek oturumu; izinli işletmeler ve oturum geçmişi.
  {
    id: 'support',
    path: '/support',
    title: 'backoffice.nav.support',
    permission: PlatformPermissions.SupportSession,
    nav: { icon: LifeBuoy },
    lazy: async () => ({ Component: (await import('../features/support/support.ts')).SupportPage }),
  },
  {
    id: 'flags',
    path: '/flags',
    title: 'backoffice.nav.flags',
    permission: PlatformPermissions.SystemRead,
    nav: { icon: Flag },
    lazy: async () => ({ Component: (await import('../features/flags/FlagsPage.tsx')).FlagsPage }),
  },
  // Yasal parametreler ve resmî tatil takvimi (S23): görmek `platform.legal.read`, değiştirmek ekranda `platform.legal.manage`.
  {
    id: 'legal',
    path: '/legal',
    title: 'backoffice.nav.legal',
    permission: PlatformPermissions.LegalRead,
    nav: { icon: Scale },
    lazy: async () => ({ Component: (await import('../features/legal/LegalSetsPage.tsx')).LegalSetsPage }),
  },
  {
    id: 'legalSet',
    path: '/legal/:id',
    title: 'backoffice.nav.legalSet',
    permission: PlatformPermissions.LegalRead,
    parent: 'legal',
    lazy: async () => ({ Component: (await import('../features/legal/LegalSetPage.tsx')).LegalSetPage }),
  },
  {
    id: 'holidays',
    path: '/holidays',
    title: 'backoffice.nav.holidays',
    permission: PlatformPermissions.LegalRead,
    nav: { icon: CalendarDays },
    lazy: async () => ({ Component: (await import('../features/legal/HolidayCalendarsPage.tsx')).HolidayCalendarsPage }),
  },
  {
    id: 'holidayYear',
    path: '/holidays/:calendarId/:year',
    title: 'backoffice.nav.holidayYear',
    permission: PlatformPermissions.LegalRead,
    parent: 'holidays',
    lazy: async () => ({ Component: (await import('../features/legal/HolidayYearPage.tsx')).HolidayYearPage }),
  },
  {
    id: 'holidayVersion',
    path: '/holidays/:calendarId/versions/:versionId',
    title: 'backoffice.nav.holidayVersion',
    permission: PlatformPermissions.LegalRead,
    parent: 'holidays',
    lazy: async () => ({ Component: (await import('../features/legal/HolidayVersionPage.tsx')).HolidayVersionPage }),
  },
  // Hukuki metin sürümleri ve alt işleyenler (S44; MK-SZ-02…07, MK-KV-37): görmek `platform.legal.read`; taslak, düzenleme, yayın ve alt
  // işleyen değişikliği ekranda `platform.legal.manage`; alt işleyen duyuru taslağı `platform.announcements.manage`.
  {
    id: 'legalDocuments',
    path: '/legal-documents',
    title: 'backoffice.nav.legalDocuments',
    permission: PlatformPermissions.LegalRead,
    nav: { icon: FileText },
    lazy: async () => ({ Component: (await import('../features/legal-documents/legal-documents.ts')).LegalDocumentsPage }),
  },
  {
    id: 'legalDocument',
    path: '/legal-documents/:key',
    title: 'backoffice.nav.legalDocument',
    permission: PlatformPermissions.LegalRead,
    parent: 'legalDocuments',
    lazy: async () => ({ Component: (await import('../features/legal-documents/legal-documents.ts')).LegalDocumentPage }),
  },
  {
    id: 'subprocessors',
    path: '/subprocessors',
    title: 'backoffice.nav.subprocessors',
    permission: PlatformPermissions.LegalRead,
    nav: { icon: Network },
    lazy: async () => ({ Component: (await import('../features/legal-documents/legal-documents.ts')).SubprocessorsPage }),
  },
  // E-posta (SMTP) ayarı (S62-d, SK-21): sunucu, port, şifreleme, kullanıcı, şifre (kayıtlı olup olmadığı görünür), gönderici; gerekçeli kayıt ve
  // deneme e-postası. Yalnız platform sahibi (`platform.settings.email`).
  {
    id: 'emailSettings',
    path: '/email-settings',
    title: 'backoffice.nav.emailSettings',
    permission: PlatformPermissions.EmailSettingsManage,
    nav: { icon: Mail },
    lazy: async () => ({ Component: (await import('../features/email-settings/EmailSettingsPage.tsx')).EmailSettingsPage }),
  },
  {
    id: 'users',
    path: '/users',
    title: 'backoffice.nav.users',
    permission: PlatformPermissions.UsersManage,
    nav: { icon: UsersRound },
    lazy: async () => ({ Component: (await import('../features/users/UsersPage.tsx')).UsersPage }),
  },
  {
    id: 'audit',
    path: '/audit',
    title: 'backoffice.nav.audit',
    permission: PlatformPermissions.AuditRead,
    nav: { icon: ScrollText },
    lazy: async () => ({ Component: (await import('../features/audit/AuditPage.tsx')).AuditPage }),
  },
];

export function routeById(id: string): RouteDef | undefined {
  return routes.find((route) => route.id === id);
}

/** Kenar çubuğunun öğeleri: oturumun yetkisi olan menü sayfaları (yetkiler `GET /api/platform/me`'den). */
export function navItemsFor(permissions: ReadonlySet<string>): RouteDef[] {
  return routes.filter((route) => route.nav && permissions.has(route.permission));
}
